import Moment from 'react-moment';
import {
  Box, Grid, Typography,
} from '@mui/material';
import { History, Unarchive } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { getFullName } from '../../../components/HistoryMainPanel/utils';
import { Briefcase } from '../../../icons/briefcase';
import { Location } from '../../../icons/location';
import { OutlineClock } from '../../../icons/outlineClock';
import { DATE_FORMAT } from '../../../consts';
import STRING_KEYS from '../../../language/keys';
import PerfectButton from '../../../components/common/buttons/PerfectButton';
import { useAuth } from '../../../hooks/useAuth';
import { PERMISSIONS } from '../../../consts/permissions';
import PositionStatus from '../../../components/PositionStatus';
import { UserCircle } from '../../../icons/user-circle';
import PositionLocation from '../../../components/PositionDetails/position-location/position-location.component';
import { Archive } from '../../../icons/archive';
import { PositionStateEnum, VIEW_MODE } from '../../../consts/enums';
import { PositionCloseDialog } from '../../../components/PositionDetails/PositionCloseDialog';
import { PositionReopenDialog } from '../../../components/PositionDetails/PositionReopenDialog';
import { Position, PositionState } from '../../../@types/position';
import style from './style';

type PositionDetailsHeaderType = {
  position: Position | undefined,
  onViewHistoryClick: () => void,
  onClosePositionHandler: (positonState: PositionState) => void,
}

const {
  grid, box, titleCompanyName, titleText, subTitle, action,
} = style;

export default function PositionDetailsHeader({
  position,
  onViewHistoryClick,
  onClosePositionHandler,
}: PositionDetailsHeaderType): JSX.Element {
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();
  const [isPositionCloseDialogVisible, setPositionCloseDialogVisibility] = useState(false);
  const [isPositionReopenDialogVisible, setPositionReopenDialogVisibility] = useState(false);

  const handlePositionCloseClick = () => {
    setPositionCloseDialogVisibility(true);
  };
  const handleHidePositionCloseDialog = () => {
    setPositionCloseDialogVisibility(false);
  };

  const handlePositionReopenClick = () => {
    setPositionReopenDialogVisibility(true);
  };
  const handleHidePositionReopenDialog = () => {
    setPositionReopenDialogVisibility(false);
  };

  const isPositionClosed = position?.state === PositionStateEnum.Closed;

  return (
    <>
      <Grid
        container
        justifyContent={ grid.justifyContent }
        spacing={ grid.spacing }
      >
        <Grid item sx={ grid.sx }>
          <div>
            <Box sx={ box.sx }>
              <Briefcase />
              <Typography variant={ titleText.variant } sx={ titleText.sx  }>
                {position?.jobTitle}
              </Typography>
              <Typography variant={ titleCompanyName.variant } sx={ titleCompanyName.sx }>
                {position?.accountName}
              </Typography>
            </Box>
            <Box sx={ { marginTop: '5px' } }>
              <Typography
                variant={ subTitle.dateBox.text.variant }
                sx={ subTitle.dateBox.text.sx }
              >
                Account Id: {position?.accountId}
              </Typography>
            </Box>
            <Box sx={ subTitle.box.sx }>
              <PositionStatus
                closedAt={ position?.positionClosure?.closedAt || '' }
                closedByUserId={ position?.positionClosure?.closedBy?.userId || '' }
                closureReason={ position?.positionClosure?.closureReason || '' }
                closureReasonfreeText={ position?.positionClosure?.freeText || '' }
                state={ position?.state || '' }
                status={ position?.status }
                isNew={ false }
              />
              <Box sx={ subTitle.locationBox.sx }>
                <Location />
                <Typography
                  variant={ subTitle.locationBox.locationText.variant }
                  sx={ subTitle.locationBox.locationText.sx }
                >
                  { position && <PositionLocation position={ position } /> }
                </Typography>
              </Box>
              <Box sx={ subTitle.dateBox.sx }>
                <OutlineClock />
                <Typography
                  variant={ subTitle.dateBox.text.variant }
                  sx={ subTitle.dateBox.text.sx }
                >
                  {
                    translate(STRING_KEYS.POSITION_DETAILS_SUB_TITLE_CREATE,
                      { name: getFullName(position?.createdBy) })
                  }
                </Typography>
                <Typography
                  variant={ subTitle.dateBox.text.variant }
                  sx={ subTitle.dateBox.text.sx }
                >
                  <Moment format={ DATE_FORMAT.TABLE_LIST }>
                    {position?.createdAt}
                  </Moment>
                </Typography>
              </Box>
            </Box>
          </div>
        </Grid>

        <Grid
          item
          sx={ action.grid.sx }
        >
          <div>
            <Box sx={ box.sx }>
              {hasPermissions([PERMISSIONS.POSITION_HISTORY_LOG_VIEW]) && (
                <PerfectButton
                  type="icon"
                  onClick={ onViewHistoryClick }
                  icon={ <History />  }
                />
              )}
              {hasPermissions([PERMISSIONS.POSITION_EDIT]) && (
                <>
                  {isPositionClosed ? (
                    <PerfectButton
                      type="icon"
                      onClick={ handlePositionReopenClick }
                      icon={ <Unarchive />  }
                    />
                  ) : (
                    <PerfectButton
                      type="icon"
                      onClick={ handlePositionCloseClick }
                      icon={ <Archive />  }
                    />
                  )}
                </>
              )}
            </Box>
            {(position?.internalOwner) && (
              <Box
                sx={ subTitle.internalUserBox.sx }
                justifyContent={ subTitle.internalUserBox.justifyContent }
              >
                <UserCircle />
                <Typography
                  variant={ subTitle.internalUserBox.text.variant }
                  sx={ subTitle.internalUserBox.text.sx }
                >
                  {position?.internalOwner}
                </Typography>
              </Box>
            )}
          </div>
        </Grid>

      </Grid>

      <PositionCloseDialog
        onCloseDialog={ handleHidePositionCloseDialog }
        isOpen={ isPositionCloseDialogVisible }
        viewMode={ VIEW_MODE.EDITABLE }
        onPositionStateChange={ (values) => {
          handleHidePositionCloseDialog();
          onClosePositionHandler(values);
        } }
      />

      <PositionReopenDialog
        onCloseDialog={ handleHidePositionReopenDialog }
        isOpen={ isPositionReopenDialogVisible }
        onPositionStateChange={ (values) => {
          handleHidePositionReopenDialog();
          onClosePositionHandler(values);
        } }
      />
    </>
  );
}
